import React, {
  FunctionComponent,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import ChipInput from 'material-ui-chip-input';
import {
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  TextField,
  Table,
  TableRow,
  TableBody,
  TableCell,
  TableHead,
  Grid,
  Paper,
  Divider,
  FormControlLabel,
  Hidden,
  Switch,
} from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import { onlyUnique } from '../../../../../utils/helpers';
import { useManageContext } from '../../../../../contexts/ManageContext/ManageContext';
import { ExpandLess, ExpandMore, InfoOutlined } from '@material-ui/icons';
import { makeStyles } from '@material-ui/styles';
import { Theme } from '@material-ui/core/styles';
import { Parser } from 'steplix-query-filters';
import {
  MediaDown,
  MediaUp,
} from '../../../../../components/MediaQueries/MediaQueries';
import ClearAllIcon from '@material-ui/icons/ClearAll';
import { Autocomplete } from '@material-ui/lab';
import Visible from '../../../../../components/Visible/Visible';
import MedPopper from '../../../../../components/MedPopper/MedPopper';
import { useSnackbar } from 'notistack';
import { useAppDispatch } from '../../../../../hooks';
import { setDeviceFilters } from '../../../../../store/app-slice';
import { useAuthUser } from '../../../../../contexts/AuthProvider/hooks';
import { useGetReportsQuery } from '../../../../../store/api-slice';
import { useConfig } from '../../../../../eliot-components/src/ConfigProvider/hooks';
import { useHistory } from 'react-router-dom';

const useStyles = makeStyles((theme: Theme) => ({
  advancedInput: {
    '&>div': {
      height: '47px',
    },
    '& input': {
      paddingTop: '0',
    },
  },
  paper: {
    padding: theme.spacing(1),
    textAlign: 'center',
    color: theme.palette.text.secondary,
  },
  params: {
    marginTop: theme.spacing(4),
    fontWeight: 500,
  },
  advancedCheckbox: {
    minWidth: '155px',
    marginRight: '0',
    marginTop: '5px',
  },
  ChipInput: {
    '& input': {
      marginTop: '7px',
    },
  },
  ChipInputSmall: {
    height: '50px',
    overflow: 'auto',
    '&>div': {
      '&:before': {
        transition: 'none !important',
        borderBottom: 'none !important',
      },
      '&:after': {
        borderBottom: 'none !important',
      },
    },
    '& input': {
      marginTop: '7px',
    },
  },
  expand: {
    textAlign: 'center',
  },
  SearchWrapper: {
    '& button': {
      backgroundColor: 'transparent !important',
      '&:hover': {
        backgroundColor: 'transparent !important',
        color: theme.palette.primary.main,
      },
    },
  },
  searchForm: {
    display: 'flex',
    alignItems: 'center',
    minHeight: theme.mixins.toolbar.minHeight,
    [theme.breakpoints.down('xs')]: {
      minHeight: '56px',
      alignItems: 'center',
    },
  },
}));

interface Props {
  searchQuery: string[];
  inputRef?: React.Ref<any>;
  advancedSearchOpen: boolean;
}

export enum Operations {
  EQUAL = '=',
  NOTEQUAL = '!=',
  GREATERTHEN = '>',
  GREATEREQUALTHEN = '>=',
  LESSEQUALTHEN = '<=',
  LESSTHEN = '<',
  LIKE = 'like',
  NOTLIKE = '!like',
  IN = 'in',
  NOTIN = '!in',
  AND = 'and',
}

const keywords = [
  {
    key: Operations.EQUAL,
    example: 'model = BP800',
    description: 'Equality',
  },
  {
    key: Operations.NOTEQUAL,
    example: 'model != BP800',
    description: 'Inequality',
  },
  {
    key: Operations.GREATERTHEN,
    example: 'measurementCount > 1',
    description: 'Greater than',
  },
  {
    key: Operations.GREATEREQUALTHEN,
    example: 'measurementCount >= 1',
    description: 'Greater than or equal to',
  },
  {
    key: Operations.LESSTHEN,
    example: 'measurementCount < 1',
    description: 'Less than',
  },
  {
    key: Operations.LESSEQUALTHEN,
    example: 'measurementCount <= 1',
    description: 'Less than or equal to',
  },
  {
    key: Operations.LIKE,
    example: 'model like BC',
    description: 'Contains',
  },
  {
    key: Operations.NOTLIKE,
    example: 'model !like BC',
    description: 'Does not contain',
  },
  {
    key: Operations.IN,
    example: 'model in BC800, D40G',
    description: 'Any listed value',
  },
  {
    key: Operations.NOTIN,
    example: 'model !in BC800, D40G',
    description: 'Any unlisted value',
  },
  {
    key: Operations.AND,
    example: 'imei like 1000 and model like 800',
    description: 'Combine multiple expressions',
  },
];

const params = [
  'model',
  'serialNumber',
  'version',
  'imei',
  'orgName',
  'measurementCount',
  'mac',
  'iccid',
  'orgID',
];

// "a, b  c,d" -> ['a', 'b', 'c', 'd']
const splitTerms = (value: string) =>
  value.split(/[\s,]+/).filter((v) => v !== '');

function getRefInput(ref: React.RefObject<any>) {
  const input = ref.current?.getElementsByTagName('input');
  if (input && input.length > 0) {
    return input[0];
  }
}

const SearchInput: FunctionComponent<Props> = ({
  searchQuery,
  inputRef,
  advancedSearchOpen,
}) => {
  const advancedSearchRef = useRef<HTMLInputElement>();
  const parser = new Parser({
    operators: [
      Operations.EQUAL,
      Operations.NOTEQUAL,
      Operations.GREATERTHEN,
      Operations.GREATEREQUALTHEN,
      Operations.LESSTHEN,
      Operations.LESSEQUALTHEN,
      Operations.LIKE,
      Operations.NOTLIKE,
      Operations.IN,
      Operations.NOTIN,
    ],
    separator: /and\s?/,
    separatorGroups: /,\s?/,
  });

  const classes = useStyles();
  const config = useConfig();
  const { authUser } = useAuthUser();
  const { state, dispatch } = useManageContext();
  const { enqueueSnackbar } = useSnackbar();
  const dispatchRedux = useAppDispatch();
  const [query, setQuery] = useState<string[]>(searchQuery);
  const [searchFocused, setSearchFocused] = useState(false);
  const [searchExpanded, setSearchExpanded] = useState(false);
  const [queryInput, setQueryInput] = useState('');
  const [advancedQuery, setAdvancedQuery] = useState<string>(
    state.advancedSearchQuery || ''
  );
  const [advancedSearchTimer, setAdvancedSearchTimer] = useState<number | null>(
    null
  );
  const [advancedSearch, setAdvancedSearch] = useState(
    !!state.advancedSearchQuery || advancedSearchOpen
  );
  const [generatedOptions, setGeneratedOptions] = useState<string[]>(params);
  const [disableDashboardFilter, setDisableDashboardFilters] = useState(false);
  const [infoOpen, setInfoOpen] = useState(false);
  const { data: reports = [] } = useGetReportsQuery({
    apiBaseURL: config.apiBaseURL,
    orgs: [],
    filters: [
      {
        category: 'archived',
        value: 'true',
      },
    ],
  });

  const deviceNum = useMemo(
    () =>
      reports
        .map((report) => {
          return report.deviceCount ? report.deviceCount : 0;
        })
        .reduce((a, b) => a + b, 0),
    [reports]
  );
  useEffect(() => {
    if (deviceNum > 8000 || authUser.orgs.length > 99) {
      setDisableDashboardFilters(true);
    }
  }, [deviceNum, authUser.orgs.length]);
  const history = useHistory();

  useEffect(() => {
    if (advancedSearch && advancedSearchRef.current) {
      const input = getRefInput(advancedSearchRef);
      if (input) {
        input.focus();
      }
    }
    localStorage.setItem('advancedSearch', JSON.stringify(advancedSearch));
  }, [advancedSearch]);

  const actionSearchClosed = () => {
    const input = getRefInput(advancedSearchRef);
    if (input !== document.activeElement && !searchFocused && !infoOpen) {
      dispatch({
        type: 'search-closed',
      });
    }
  };

  const actionQueryChanged = (e: any) => {
    e.preventDefault();

    if (!advancedSearch) {
      dispatch({
        type: 'search-query-changed',
        data: query,
      });
    }
  };

  const parseExpression = (
    inputValue: string,
    prevOptions: string[]
  ): string[] => {
    const input = inputValue.split(' ');
    const keyword = input.shift();
    let actualOptions: string[] = [];

    if (inputValue.trim() === '') {
      actualOptions = params;
    } else {
      actualOptions = params
        .filter((o) => (keyword ? o.indexOf(keyword) !== -1 : false))
        .map((o) => `${o} ${input.join(' ')}`);
    }

    if (prevOptions.length > 0) {
      return prevOptions
        .map((p) => actualOptions.map((a) => `${p.trim()} and ${a}`))
        .flat(1);
    }
    return actualOptions;
  };

  const handleAdvancedSearch = (e: any, value: string | null) => {
    if (advancedSearchTimer != null) {
      window.clearTimeout(advancedSearchTimer);
      setAdvancedSearchTimer(null);
    }
    if (value) {
      const expressions = value.split(/and\s?/);
      let go: string[] = [];
      expressions.forEach((e) => {
        go = parseExpression(e, go);
      });
      setGeneratedOptions(go);
    } else {
      setGeneratedOptions(params);
    }
    setAdvancedQuery(value ? value : '');
  };

  const queryChanged = (newQuery: string[]) => {
    setQuery(newQuery);
    if (!disableDashboardFilter) {
      dispatchRedux(
        setDeviceFilters(
          newQuery
            .filter((val) => !!val)
            .map((val) => ({
              category: 'serial_number_or_imei',
              value: val,
            }))
        )
      );
    }

    dispatch({
      type: 'search-query-changed',
      data: newQuery,
    });
  };

  const handleAddChip = (chip: string) => {
    let needClear = true;
    let notClear = '';
    const newChips = splitTerms(chip).filter((c) => {
      if (c.length > 23 || c.length < 5) {
        enqueueSnackbar(
          'Search term must be between 5 and 23 characters long',
          { variant: 'warning' }
        );
        needClear = false;
        return false;
      }
      return true;
    });

    if (needClear) {
      setQueryInput('');
    } else {
      // trim last ,
      setQueryInput(notClear.substring(0, notClear.length - 1));
    }

    let newQuery = [...query, ...newChips].filter(onlyUnique());
    if (newQuery.length > 50) {
      enqueueSnackbar('Filter can consist with max of 50 terms', {
        variant: 'warning',
      });
      newQuery = newQuery.slice(0, 50);
    }
    queryChanged(newQuery);
  };
  const handleDeleteChip = (chip: string, index: number) => {
    queryChanged(query.filter((q, i) => i !== index));
  };

  const isValidAdvancedFilter = (
    filters: Filters,
    filter: string,
    keys: string[]
  ) => {
    const validateOnly = ['serialNumber', 'imei'];
    if (!validateOnly.includes(filter)) {
      return true;
    }
    if (keys.indexOf(Operations.EQUAL) > -1) {
      //@ts-ignore
      return filters[filter][Operations.EQUAL].length >= 5;
    }
    if (keys.indexOf(Operations.LIKE) > -1) {
      //@ts-ignore
      return filters[filter][Operations.LIKE].length >= 5;
    }

    return true;
  };

  const submitAdvancedSearch = () => {
    let lastParam = advancedQuery.trim().split(' ').pop();
    const param = (lastParam as string).trim();
    if (
      // @ts-ignore
      Object.values(Operations).includes(param) ||
      params.indexOf(param) !== -1 ||
      param === 'and'
    ) {
      return;
    }
    const filters = parser.parse(advancedQuery);
    let alert = false;
    let error = null;
    let validFilter = true;

    if (!filters || (filters && Object.keys(filters).length === 0)) return;
    if (advancedQuery === state.advancedSearchQuery) return;
    Object.keys(filters).forEach((filter) => {
      // @ts-ignore
      const keys = Object.keys(filters[filter]);
      if (!isValidAdvancedFilter(filters, filter, keys)) {
        validFilter = false;
        enqueueSnackbar('Search query must be at least five characters long.', {
          variant: 'warning',
        });
        return;
      }

      if (
        keys.indexOf(Operations.LIKE) > -1 ||
        keys.indexOf(Operations.NOTLIKE) > -1 ||
        keys.indexOf(Operations.NOTEQUAL) > -1 ||
        keys.indexOf(Operations.NOTIN) > -1 ||
        filter === 'orgId' ||
        filter === 'orgName'
      ) {
        alert = true;
      }
      if (
        keys.indexOf(Operations.IN) > -1 &&
        // @ts-ignore
        filters[filter][Operations.IN].length > 50
      ) {
        enqueueSnackbar('Filter can consist with max of 50 terms', {
          variant: 'warning',
        });
        // @ts-ignore
        filters[filter][Operations.IN] = filters[filter][Operations.IN].slice(
          0,
          50
        );
        setAdvancedQuery(parser.format(filters));
      }
    });

    if (!validFilter) {
      return;
    }

    dispatch({
      type: 'advanced-search-query-changed',
      data: {
        filters,
        alert,
        error,
        query: advancedQuery,
      },
    });
  };

  const keywordTable = keywords.map((keyword) => {
    return (
      <TableRow key={keyword.key}>
        <TableCell>{keyword.key}</TableCell>
        <TableCell>{keyword.example}</TableCell>
        <Hidden smDown>
          <TableCell>{keyword.description}</TableCell>
        </Hidden>
      </TableRow>
    );
  });

  const clearAllSearch = () => {
    dispatch({
      type: 'search-query-changed',
      data: null,
    });
    dispatch({
      type: 'advanced-search-query-changed',
      data: {
        filters: null,
        query: null,
      },
    });
    setQuery([]);
    setQueryInput('');
    setAdvancedQuery('');
    if (!disableDashboardFilter) {
      dispatchRedux(setDeviceFilters([]));
    }
    history.push({ search: '' });
  };

  return (
    <div
      onMouseLeave={() =>
        (query && query.length) || advancedSearch ? null : actionSearchClosed()
      }
      className={classes.SearchWrapper}
    >
      <form
        onSubmit={actionQueryChanged}
        className={classes.searchForm}
        data-testid="advanced-search-id"
      >
        {advancedSearch ? (
          <Autocomplete
            ListboxProps={{ style: { maxHeight: '100%' } }}
            PopperComponent={MedPopper}
            fullWidth
            freeSolo
            value={advancedQuery}
            disabled={
              state.loadingDevices &&
              advancedQuery != null &&
              advancedQuery.length > 0
            }
            className={classes.advancedInput}
            data-testid="advanced-search-input"
            ref={advancedSearchRef}
            onInputChange={handleAdvancedSearch}
            disableClearable
            onClose={() => {
              const timer = window.setTimeout(() => {
                submitAdvancedSearch();
              }, 1000);
              setAdvancedSearchTimer(timer);
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                margin="dense"
                rows={2}
                data-testid="advanced-search-text-field"
                placeholder="Advanced search"
                style={{ height: '40px' }}
                inputProps={{
                  ...params.inputProps,
                  rows: 2,
                  style: { height: '34px' },
                }}
              />
            )}
            options={generatedOptions}
          />
        ) : (
          <ChipInput
            ref={inputRef}
            style={{ flex: 1, marginTop: '15px' }}
            newChipKeys={[' ', ', ', ',']}
            value={query}
            inputValue={queryInput}
            onBeforeAdd={(e: string) => {
              const q = splitTerms(queryInput)
                .filter((i) => query.indexOf(i) === -1)
                .join(',');

              if (q !== queryInput) {
                setQueryInput(q);
              }

              return true;
            }}
            onUpdateInput={(e) => {
              const values = splitTerms(e.target.value);
              if (values.some((value) => value.length > 23)) {
                enqueueSnackbar(
                  'Search term must be between 5 and 23 characters long',
                  { variant: 'warning' }
                );
              }
              setQueryInput(values.join(', '));
            }}
            data-testid="search-input"
            onAdd={handleAddChip}
            onDelete={handleDeleteChip}
            onFocus={() => setSearchFocused(true)}
            onBlur={() => setSearchFocused(false)}
            className={
              searchExpanded ? classes.ChipInput : classes.ChipInputSmall
            }
            placeholder="Search IMEI, serial number or MAC"
          />
        )}
        <Visible when={query.length > 8}>
          <IconButton
            onClick={() => setSearchExpanded(!searchExpanded)}
            disableRipple={true}
          >
            {searchExpanded ? <ExpandLess /> : <ExpandMore />}
          </IconButton>
        </Visible>
        <MediaUp query="sm">
          <Visible when={advancedSearch}>
            <IconButton onClick={() => setInfoOpen(true)} title="Syntax">
              <InfoOutlined />
            </IconButton>
          </Visible>
          <FormControlLabel
            className={classes.advancedCheckbox}
            data-testid="advanced-search-checkbox"
            control={
              <Switch
                checked={advancedSearch}
                color="primary"
                onChange={() => {
                  clearAllSearch();
                  setAdvancedSearch(!advancedSearch);
                }}
              />
            }
            label="Advanced"
          />
        </MediaUp>
        <MediaUp query="sm">
          <IconButton
            title="Remove all search queries"
            disableRipple={true}
            onClick={clearAllSearch}
            edge="end"
          >
            <ClearAllIcon />
          </IconButton>
        </MediaUp>
        <IconButton
          edge="end"
          onClick={actionSearchClosed}
          title="Close"
          disableRipple={true}
        >
          <CloseIcon />
        </IconButton>
        <Dialog
          open={infoOpen}
          onClose={() => setInfoOpen(false)}
          maxWidth="md"
        >
          <DialogTitle>Advanced Search Syntax</DialogTitle>
          <DialogContent>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Operator</TableCell>
                  <TableCell>Example</TableCell>
                  <Hidden smDown>
                    <TableCell>Description</TableCell>
                  </Hidden>
                </TableRow>
              </TableHead>
              <TableBody>{keywordTable}</TableBody>
            </Table>
            <div className={classes.params}>Parameters</div>
            <Divider
              variant="fullWidth"
              style={{ marginTop: '10px', marginBottom: '20px' }}
            />
            <Grid container spacing={1}>
              <Grid container item xs={12} spacing={3}>
                {params.map((param) => (
                  <Grid item xs={12} sm={6} md={4} key={param}>
                    <Paper className={classes.paper}>{param}</Paper>
                  </Grid>
                ))}
              </Grid>
            </Grid>
          </DialogContent>
        </Dialog>
      </form>
      <MediaDown query="xs">
        <IconButton
          onClick={() => setInfoOpen(true)}
          title="Help"
          disableRipple={true}
        >
          <InfoOutlined />
        </IconButton>
        <FormControlLabel
          data-testid="advanced-search-checkbox-mobile"
          control={
            <Switch
              checked={advancedSearch}
              color="primary"
              onChange={() => {
                clearAllSearch();
                setAdvancedSearch(!advancedSearch);
              }}
            />
          }
          label="Advanced"
        />
        <IconButton
          title="Remove all search queries"
          disableRipple={true}
          onClick={clearAllSearch}
          edge="end"
        >
          <ClearAllIcon />
        </IconButton>
      </MediaDown>
    </div>
  );
};

export default SearchInput;
